import { STOCKTAKE_ROLES } from '../../constants/stocktakeRoles';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import './Sidebar.css';
import { List, ListItem, ListItemText, Collapse, IconButton } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const itemRoutes = ['/item-categories', '/items', '/stock-in', '/stock-out', '/stocks', '/low-stock', '/stocktakes', '/stock-transfers', '/stock-history', '/unit-of-measurement'];
    const accountingRoutes = ['/accounting/'];
    const procurementRoutes = ['/procurement/'];
    const salesRoutes = ['/sales/'];

    const [itemMenuOpen, setItemMenuOpen] = useState(() => itemRoutes.some(r => location.pathname.startsWith(r)));
    const [accountingMenuOpen, setAccountingMenuOpen] = useState(() => accountingRoutes.some(r => location.pathname.startsWith(r)));
    const [procurementMenuOpen, setProcurementMenuOpen] = useState(() => procurementRoutes.some(r => location.pathname.startsWith(r)));
    const [salesMenuOpen, setSalesMenuOpen] = useState(() => salesRoutes.some(r => location.pathname.startsWith(r)));
    // POS is this product's primary module, so its group starts EXPANDED regardless of route --
    // unlike the inventory/accounting/procurement/sales groups below, which open only on their own
    // routes. A collapsed POS group was indistinguishable from the inherited back-office modules.
    const [posMenuOpen, setPosMenuOpen] = useState(true);
    const settingsRoutes = ['/users', '/stores', '/institutions', '/all-institution-users',
        '/all-institution-stores', '/institution-settings', '/billing', '/saas/', '/operator/'];
    const [settingsMenuOpen, setSettingsMenuOpen] = useState(
        () => settingsRoutes.some(r => location.pathname.startsWith(r)));
    const [dashboardMenuOpen, setDashboardMenuOpen] = useState(() => location.pathname === '/' || location.pathname.startsWith('/dashboard'));

    // Convert user roles to array for checking
    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];

    // Check if user has specific role
    const hasRole = (role: UserType) => userRoles.includes(role);

    // Check permissions
    const isSystemAdmin = hasRole(UserType.SystemAdmin);
    const isInstitutionAdmin = hasRole(UserType.InstitutionAdmin);
    const isInstitutionManager = hasRole(UserType.InstitutionManager);
    const isStoreAdmin = hasRole(UserType.StoreAdmin);
    const isStoreManager = hasRole(UserType.StoreManager);
    const isAccountant = hasRole(UserType.Accountant);
    const canAccessPos = isInstitutionAdmin || isStoreAdmin || isStoreManager || isAccountant;
    // Narrower than canAccessPos: PosSettingsController's GET is InstitutionAdmin/StoreAdmin/
    // Accountant only, StoreManager excluded. The section stays open to StoreManager (the
    // terminals/cashiers/sales controllers Tasks 3-6 add all admit it) but the Settings link
    // itself must not point a StoreManager at a screen that 403s.
    const canAccessPosSettings = isInstitutionAdmin || isStoreAdmin || isAccountant;
    // PosTerminalController gates every endpoint to InstitutionAdmin/StoreAdmin/StoreManager only
    // -- notably no Accountant, the opposite narrowing from Settings above.
    const canAccessPosTerminals = isInstitutionAdmin || isStoreAdmin || isStoreManager;
    // PosStoreAssignmentController and PosCashierPinController both gate every endpoint to the
    // same set as PosTerminalController -- InstitutionAdmin/StoreAdmin/StoreManager, no
    // Accountant. Kept as its own const (identical value today) so the two controllers drifting
    // apart in future is a one-line diff here, not a silent shared-gate bug.
    const canAccessPosCashiers = isInstitutionAdmin || isStoreAdmin || isStoreManager;
    // PosSaleQueryController's list/detail endpoints admit all four POS roles -- InstitutionAdmin,
    // StoreAdmin, StoreManager and Accountant -- so this link needs no narrower gate than
    // canAccessPos itself (verified against the controller's @PreAuthorize, task-5).
    //
    // Task 7: the unposted queue's GET /unposted and POST /{id}/post are narrower --
    // InstitutionAdmin/StoreAdmin/Accountant only, StoreManager deliberately EXCLUDED (unlike
    // every other POS link above, which admits StoreManager). A StoreManager who followed this
    // link would 403 on load, so it must not be gated by canAccessPos alone.
    const canAccessPosUnposted = isInstitutionAdmin || isStoreAdmin || isAccountant;
    const isSaaSOperator = hasRole(UserType.SaaSOperator);

    // Board roles get an expandable "Dashboard" menu (Overview + module deep-pages);
    // everyone else keeps the flat Dashboard link. The module sub-links are gated by the
    // user's role-registry modules intersected with the institution's enabled set, so a
    // restricted user only sees their modules.
    const isDashboardBoardRole = isInstitutionAdmin || isInstitutionManager || isAccountant;
    const { visibleModules } = useDashboardAccess();

    // Fine-grained permission checks (manual overrides via usePermission)
    const canAccessDashboard = usePermission(PERMISSIONS.MENU_DASHBOARD, true);
    const canAccessUsers = usePermission(PERMISSIONS.MENU_USERS, isInstitutionAdmin);
    const canAccessStores = usePermission(PERMISSIONS.MENU_STORES, isInstitutionAdmin);
    const canAccessAccounting = usePermission(PERMISSIONS.MENU_ACCOUNTING, isAccountant || isInstitutionAdmin);
    const canAccessItemManagement = usePermission(PERMISSIONS.MENU_ITEMS, isInstitutionAdmin || isStoreAdmin);
    const canAccessStocktakes = usePermission(PERMISSIONS.STOCKTAKE_VIEW, STOCKTAKE_ROLES.some(hasRole));
    const canAccessStockTransfers = usePermission(PERMISSIONS.STOCK_TRANSFER_VIEW, isInstitutionAdmin || isStoreAdmin);

    const handleItemMenuClick = () => {
        setItemMenuOpen(!itemMenuOpen);
    };

    const handleAccountingMenuClick = () => {
        setAccountingMenuOpen(!accountingMenuOpen);
    };

    // The flat top-of-nav items: just the two entry points. Everything administrative moved into
    // the collapsible Settings group at the bottom of the nav (settingsMenuItems below).
    const topMenuItems = [
        ...(canAccessDashboard && !isDashboardBoardRole ? [{ path: '/', label: 'Dashboard', icon: '📊' }] : []),

        // Launcher page listing every module the user can reach — visible to everyone.
        { path: '/modules', label: 'All Modules', icon: '🧩' },
    ];

    // Administration, grouped under one collapsible "Settings" heading and rendered LAST, after
    // every operational module. These are configuration screens a shop touches rarely; before this
    // they sat flat and directly under the launcher, above the work the product exists to do.
    // Each entry keeps the exact role gate it had as a flat item.
    const settingsMenuItems = [
        // Users / Stores — Institution Admin permission
        ...(canAccessUsers ? [
            { path: '/users', label: 'Users', icon: '👥' },
        ] : []),
        ...(canAccessStores ? [
            { path: '/stores', label: 'Stores', icon: '🏪' },
        ] : []),

        // Institutions menu - only for System Admin (no permission override needed)
        ...(isSystemAdmin ? [
            { path: '/institutions', label: 'Institutions', icon: '🏢' },
            { path: '/all-institution-users', label: 'All Institution Users', icon: '👥' },
            { path: '/all-institution-stores', label: 'All Institution Stores', icon: '🏪' }
        ] : []),

        // Institution Admin can edit a limited set of their own institution's settings
        ...(isInstitutionAdmin && !isSystemAdmin ? [
            { path: '/institution-settings', label: 'Institution Settings', icon: '⚙️' },
        ] : []),

        // Billing — Institution Admin only (subscription invoices + bKash checkout)
        ...(isInstitutionAdmin ? [
            { path: '/billing', label: 'Billing', icon: <ReceiptLongIcon fontSize="small" /> },
        ] : []),

        // SaaS Operator console — premium dashboard provisioning across institutions
        ...(isSaaSOperator || isSystemAdmin ? [
            { path: '/saas/premium-provisioning', label: 'Premium Provisioning', icon: '💎' },
        ] : []),

        // SaaS Operator console — subscription billing across institutions
        ...(isSaaSOperator || isSystemAdmin ? [
            { path: '/operator/billing', label: 'Subscription Billing', icon: <ReceiptLongIcon fontSize="small" /> },
        ] : []),
    ];

    const handleDashboardMenuClick = () => setDashboardMenuOpen(!dashboardMenuOpen);
    const handleProcurementMenuClick = () => setProcurementMenuOpen(!procurementMenuOpen);
    const handleSalesMenuClick = () => setSalesMenuOpen(!salesMenuOpen);
    const handlePosMenuClick = () => setPosMenuOpen(!posMenuOpen);
    const handleSettingsMenuClick = () => setSettingsMenuOpen(!settingsMenuOpen);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="sidebar" style={{ width: collapsed ? '60px' : '250px' }}>
            <div className="logo">
                <div className="logo-inner">
                    {!collapsed && <h2>Kervolt POS</h2>}
                    <IconButton onClick={onToggle} className="sidebar-toggle" size="small">
                        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </div>
            </div>
            <nav className="nav">
                {canAccessDashboard && isDashboardBoardRole && (
                <div className="dashboard-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleDashboardMenuClick}
                            title={collapsed ? 'Dashboard' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📊</span>
                            {!collapsed && <ListItemText primary="Dashboard" />}
                            {!collapsed && (dashboardMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && dashboardMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/"
                                    className={`menuItem submenu ${location.pathname === '/' ? 'active' : ''}`}
                                >
                                    <span className="icon">🏠</span>
                                    Overview
                                </Link>
                                {visibleModules.has('accounting') && (
                                    <Link
                                        to="/dashboard/accounting"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/accounting' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📒</span>
                                        Accounting
                                    </Link>
                                )}
                                {visibleModules.has('inventory') && (
                                    <Link
                                        to="/dashboard/inventory"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/inventory' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📦</span>
                                        Inventory
                                    </Link>
                                )}
                                {visibleModules.has('sales') && (
                                    <Link
                                        to="/dashboard/sales"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/sales' ? 'active' : ''}`}
                                    >
                                        <span className="icon">💼</span>
                                        Sales
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}
                {topMenuItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`menuItem ${location.pathname === item.path ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
                        title={collapsed ? item.label : ''}
                    >
                        <span className="icon">{item.icon}</span>
                        {!collapsed && item.label}
                    </Link>
                ))}

                {canAccessPos && (
                <div className="pos-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handlePosMenuClick}
                            title={collapsed ? 'POS' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🛍️</span>
                            {!collapsed && <ListItemText primary="POS" />}
                            {!collapsed && (posMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && posMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {/* Task 10 adds its own <Link> entries here, one per screen,
                                    following the exact shape of the Settings link below. */}
                                {canAccessPos && (
                                <Link
                                    to="/pos/sales"
                                    className={`menuItem submenu ${location.pathname.startsWith('/pos/sales') ? 'active' : ''}`}
                                >
                                    <span className="icon">🧾</span>
                                    Sales
                                </Link>
                                )}
                                {canAccessPosUnposted && (
                                <Link
                                    to="/pos/unposted"
                                    className={`menuItem submenu ${location.pathname === '/pos/unposted' ? 'active' : ''}`}
                                >
                                    <span className="icon">⏳</span>
                                    Unposted Sales
                                </Link>
                                )}
                                {/* PosReportController's day-end endpoint admits the SAME four
                                    roles as PosSaleQueryController (InstitutionAdmin, StoreAdmin,
                                    StoreManager, Accountant) -- deliberately wider than the
                                    unposted queue above, since an Accountant needs it to
                                    reconcile and a StoreManager is the role that runs day-end at
                                    the till. Gated by canAccessPos itself, not a narrower flag. */}
                                {canAccessPos && (
                                <Link
                                    to="/pos/day-end"
                                    className={`menuItem submenu ${location.pathname === '/pos/day-end' ? 'active' : ''}`}
                                >
                                    <span className="icon">📊</span>
                                    Day-End Report
                                </Link>
                                )}
                                {canAccessPosSettings && (
                                <Link
                                    to="/pos/settings"
                                    className={`menuItem submenu ${location.pathname === '/pos/settings' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚙️</span>
                                    Settings
                                </Link>
                                )}
                                {canAccessPosTerminals && (
                                <Link
                                    to="/pos/terminals"
                                    className={`menuItem submenu ${location.pathname === '/pos/terminals' ? 'active' : ''}`}
                                >
                                    <span className="icon">🖥️</span>
                                    Terminals
                                </Link>
                                )}
                                {canAccessPosCashiers && (
                                <Link
                                    to="/pos/cashiers"
                                    className={`menuItem submenu ${location.pathname === '/pos/cashiers' ? 'active' : ''}`}
                                >
                                    <span className="icon">🧑‍💼</span>
                                    Cashiers
                                </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}


                {/* A StoreManager has no item-management access but does run stocktakes, so the group
                    opens for either; the item links below are gated by canAccessItemManagement alone. */}
                {(canAccessItemManagement || canAccessStocktakes) && (
                <div className="item-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleItemMenuClick}
                            title={collapsed ? 'Item Management' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📦</span>
                            {!collapsed && <ListItemText primary="Item Management" />}
                            {!collapsed && (itemMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && itemMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {canAccessItemManagement && (
                                <>
                                <Link
                                    to="/item-categories"
                                    className={`menuItem submenu ${location.pathname === '/item-categories' ? 'active' : ''}`}
                                >
                                    <span className="icon">🏷️</span>
                                    Item Categories
                                </Link>
                                <Link
                                    to="/items"
                                    className={`menuItem submenu ${location.pathname === '/items' ? 'active' : ''}`}
                                >
                                    <span className="icon">📦</span>
                                    Items
                                </Link>
                                <Link
                                    to="/stocks"
                                    className={`menuItem submenu ${location.pathname === '/stocks' ? 'active' : ''}`}
                                >
                                    <span className="icon">📊</span>
                                    Stocks
                                </Link>
                                <Link
                                    to="/low-stock"
                                    className={`menuItem submenu ${location.pathname === '/low-stock' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚠️</span>
                                    Low Stock
                                </Link>
                                </>
                                )}
                                {canAccessStocktakes && (
                                    <Link
                                        to="/stocktakes"
                                        className={`menuItem submenu ${location.pathname.startsWith('/stocktakes') ? 'active' : ''}`}
                                    >
                                        <span className="icon">🧮</span>
                                        Stocktakes
                                    </Link>
                                )}
                                {canAccessStockTransfers && (
                                    <Link
                                        to="/stock-transfers"
                                        className={`menuItem submenu ${location.pathname.startsWith('/stock-transfers') ? 'active' : ''}`}
                                    >
                                        <span className="icon">🔄</span>
                                        Stock Transfers
                                    </Link>
                                )}
                                {canAccessItemManagement && (
                                <>
                                <Link
                                    to="/stock-in"
                                    className={`menuItem submenu ${location.pathname === '/stock-in' ? 'active' : ''}`}
                                >
                                    <span className="icon">📥</span>
                                    Stock In
                                </Link>
                                <Link
                                    to="/stock-out"
                                    className={`menuItem submenu ${location.pathname === '/stock-out' ? 'active' : ''}`}
                                >
                                    <span className="icon">📤</span>
                                    Stock Out
                                </Link>
                                <Link
                                    to="/stock-history"
                                    className={`menuItem submenu ${location.pathname === '/stock-history' ? 'active' : ''}`}
                                >
                                    <span className="icon">📜</span>
                                    Stock History
                                </Link>
                                {(isSystemAdmin || isInstitutionAdmin) && (
                                    <Link
                                        to="/unit-of-measurement"
                                        className={`menuItem submenu ${location.pathname === '/unit-of-measurement' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📏</span>
                                        Units of Measurement
                                    </Link>
                                )}
                                </>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessAccounting && (
                <div className="accounting-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleAccountingMenuClick}
                            title={collapsed ? 'Accounting' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>💰</span>
                            {!collapsed && <ListItemText primary="Accounting" />}
                            {!collapsed && (accountingMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && accountingMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/accounting/chart-of-accounts"
                                    className={`menuItem submenu ${location.pathname === '/accounting/chart-of-accounts' ? 'active' : ''}`}
                                >
                                    <span className="icon">📒</span>
                                    Chart of Accounts
                                </Link>
                                <Link
                                    to="/accounting/transactions"
                                    className={`menuItem submenu ${location.pathname === '/accounting/transactions' ? 'active' : ''}`}
                                >
                                    <span className="icon">💳</span>
                                    Transactions
                                </Link>
                                <Link
                                    to="/accounting/journal-entries"
                                    className={`menuItem submenu ${location.pathname === '/accounting/journal-entries' ? 'active' : ''}`}
                                >
                                    <span className="icon">📖</span>
                                    Journal Entries
                                </Link>
                                <Link
                                    to="/accounting/trial-balance"
                                    className={`menuItem submenu ${location.pathname === '/accounting/trial-balance' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚖️</span>
                                    Trial Balance
                                </Link>
                                <Link
                                    to="/accounting/segment-trial-balance"
                                    className={`menuItem submenu ${location.pathname === '/accounting/segment-trial-balance' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚖️</span>
                                    Segment Trial Balance
                                </Link>
                                <Link
                                    to="/accounting/income-statement"
                                    className={`menuItem submenu ${location.pathname === '/accounting/income-statement' ? 'active' : ''}`}
                                >
                                    <span className="icon">📈</span>
                                    Income Statement
                                </Link>
                                <Link
                                    to="/accounting/balance-sheet"
                                    className={`menuItem submenu ${location.pathname === '/accounting/balance-sheet' ? 'active' : ''}`}
                                >
                                    <span className="icon">📋</span>
                                    Balance Sheet
                                </Link>
                                <Link
                                    to="/accounting/ledger-close-settings"
                                    className={`menuItem submenu ${location.pathname === '/accounting/ledger-close-settings' ? 'active' : ''}`}
                                >
                                    <span className="icon">🔒</span>
                                    Close Books Through
                                </Link>
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {(isInstitutionAdmin || isInstitutionManager || isStoreAdmin || isAccountant) && (
                <div className="procurement-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleProcurementMenuClick}
                            title={collapsed ? 'Procurement' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🛒</span>
                            {!collapsed && <ListItemText primary="Procurement" />}
                            {!collapsed && (procurementMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && procurementMenuOpen} timeout="auto" unmountOnExit>
                            {/* Each gate mirrors the @PreAuthorize of the controller the page reads from, so a link is
                                shown exactly to the roles whose page loads: Supplier, PurchaseOrder and GoodsReceipt
                                reads; ProcurementSettings is InstitutionAdmin only; Supplier Bills and Payments are
                                the Accountant's work (SupplierBill/SupplierPayment create). */}
                            <List component="div" disablePadding>
                                {(isInstitutionAdmin || isStoreAdmin || isAccountant) && (
                                    <Link
                                        to="/procurement/suppliers"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/suppliers') ? 'active' : ''}`}
                                    >
                                        <span className="icon">🏪</span>
                                        Suppliers
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isStoreAdmin) && (
                                    <Link
                                        to="/procurement/catalogue"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/catalogue') ? 'active' : ''}`}
                                    >
                                        <span className="icon">📚</span>
                                        Catalogue
                                    </Link>
                                )}
                                {(isInstitutionAdmin) && (
                                    <Link
                                        to="/procurement/settings"
                                        className={`menuItem submenu ${location.pathname === '/procurement/settings' ? 'active' : ''}`}
                                    >
                                        <span className="icon">⚙️</span>
                                        Settings
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isInstitutionManager || isStoreAdmin) && (
                                    <Link
                                        to="/procurement/purchase-orders"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/purchase-orders') && !location.pathname.includes('pending-approval') ? 'active' : ''}`}
                                    >
                                        <span className="icon">🧾</span>
                                        Purchase Orders
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isInstitutionManager) && (
                                    <Link
                                        to="/procurement/purchase-orders/pending-approval"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/purchase-orders/pending-approval') ? 'active' : ''}`}
                                    >
                                        <span className="icon">⏳</span>
                                        Pending Approval
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isInstitutionManager || isStoreAdmin) && (
                                    <Link
                                        to="/procurement/goods-receipts"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/goods-receipts') ? 'active' : ''}`}
                                    >
                                        <span className="icon">🚚</span>
                                        Goods Receipts
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isAccountant) && (
                                    <Link
                                        to="/procurement/supplier-bills"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/supplier-bills') ? 'active' : ''}`}
                                    >
                                        <span className="icon">📋</span>
                                        Supplier Bills
                                    </Link>
                                )}
                                {(isInstitutionAdmin || isAccountant) && (
                                    <Link
                                        to="/procurement/supplier-payments"
                                        className={`menuItem submenu ${location.pathname.startsWith('/procurement/supplier-payments') ? 'active' : ''}`}
                                    >
                                        <span className="icon">💳</span>
                                        Supplier Payments
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {(isInstitutionAdmin || isInstitutionManager || isStoreAdmin || isAccountant) && (
                <div className="sales-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleSalesMenuClick}
                            title={collapsed ? 'Sales' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🧾</span>
                            {!collapsed && <ListItemText primary="Sales" />}
                            {!collapsed && (salesMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && salesMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/sales/customers"
                                    className={`menuItem submenu ${location.pathname.startsWith('/sales/customers') ? 'active' : ''}`}
                                >
                                    <span className="icon">👥</span>
                                    Customers
                                </Link>
                                <Link
                                    to="/sales/quotations"
                                    className={`menuItem submenu ${location.pathname.startsWith('/sales/quotations') ? 'active' : ''}`}
                                >
                                    <span className="icon">🗒️</span>
                                    Quotations
                                </Link>
                                <Link
                                    to="/sales/invoices"
                                    className={`menuItem submenu ${location.pathname.startsWith('/sales/invoices') || location.pathname.startsWith('/sales/receipts') ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    Invoices
                                </Link>
                                <Link
                                    to="/sales/settings"
                                    className={`menuItem submenu ${location.pathname === '/sales/settings' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚙️</span>
                                    Settings
                                </Link>
                                {isInstitutionAdmin && (
                                <Link
                                    to="/sales/branding"
                                    className={`menuItem submenu ${location.pathname === '/sales/branding' ? 'active' : ''}`}
                                >
                                    <span className="icon">🎨</span>
                                    Invoice Branding
                                </Link>
                                )}
                                <Link
                                    to="/sales/reports/aging"
                                    className={`menuItem submenu ${location.pathname === '/sales/reports/aging' ? 'active' : ''}`}
                                >
                                    <span className="icon">⏳</span>
                                    AR Aging
                                </Link>
                                <Link
                                    to="/sales/reports/statement"
                                    className={`menuItem submenu ${location.pathname === '/sales/reports/statement' ? 'active' : ''}`}
                                >
                                    <span className="icon">📊</span>
                                    Statement
                                </Link>
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}


                {settingsMenuItems.length > 0 && (
                <div className="settings-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleSettingsMenuClick}
                            title={collapsed ? 'Settings' : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🛠️</span>
                            {!collapsed && <ListItemText primary="Settings" />}
                            {!collapsed && (settingsMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && settingsMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {settingsMenuItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`menuItem submenu ${location.pathname === item.path ? 'active' : ''}`}
                                    >
                                        <span className="icon">{item.icon}</span>
                                        {item.label}
                                    </Link>
                                ))}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}
            </nav>
            <div className="logout-section">
                <button onClick={handleLogout} className={`logout-button ${collapsed ? 'collapsed' : ''}`} title={collapsed ? 'Logout' : ''}>
                    <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🚪</span>
                    {!collapsed && 'Logout'}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
